import React, { useEffect, useMemo } from "react";
import { merge, omit } from "lodash-es";
import { useDispatch, useSelector } from "react-redux";
import { copy } from "shared/assets";

import { addTemporaryToast } from "shared/components/Toast";
import { IAPIState, IFormButtons, IStatus } from "shared/types";
import { VenueForm } from "Venues/components";
import { useVenuePageContext } from "Venues/contexts";
import { VENUE_PATHS } from "Venues/router";
import { dataMetaSelector, editVenue } from "Venues/state";
import { IVenue, VENUE_ACTIONS } from "Venues/types";
import { useClearActionMetaOnUnMount, useVenueRoute } from "Venues/utils";

interface EditVenueProps {
  updatePlaceholder?: (venue: IVenue) => void;
}

export const EditVenue: React.FC<EditVenueProps> = ({ updatePlaceholder }) => {
  const { api: apiMeta } = useSelector(dataMetaSelector(VENUE_ACTIONS.editVenue));
  const { venueId, venue } = useVenuePageContext();
  useClearActionMetaOnUnMount(VENUE_ACTIONS.editVenue);

  const dispatch = useDispatch();
  const { pushRoute } = useVenueRoute();

  const buttons: IFormButtons | undefined = useMemo(
    () => ({
      primary: {
        "data-testid": "edit_venue_submit",
      },
      secondary: {
        "data-testid": "edit_venue_cancel",
        onClick: () => pushRoute(VENUE_PATHS.VENUE),
      },
    }),
    [pushRoute]
  );

  const defaultValues = useMemo(() => {
    if (venue) {
      const defaultValues = omit(venue, ["address.staticImage"]);
      if (venue.contact?.phone?.code === null || venue.contact?.phone?.code === undefined) {
        return merge({}, defaultValues, { contact: { phone: { code: "BE" } } });
      }
      return defaultValues;
    }
    return {};
  }, [venue]);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      pushRoute(VENUE_PATHS.VENUE);
      dispatch(
        addTemporaryToast({
          message: copy.toast.changed,
          status: IStatus.success,
        })
      );
    }
  }, [apiMeta, dispatch, pushRoute]);

  const handleSubmit = (venue: any) => {
    dispatch(editVenue({ venueId, venueData: venue }));
  };

  return (
    <VenueForm
      handleSubmit={handleSubmit}
      buttons={buttons}
      hasError={!!apiMeta?.error}
      apiStatus={apiMeta?.status}
      defaultValues={defaultValues}
      updatePlaceholder={updatePlaceholder}
      hasContainer
    />
  );
};
